import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { GitStatus } from '@common/git/types/git-status.type';
import { WorkspacePullFileOutcome, WorkspacePullFileState } from '@common/workspace/models/workspace-pull-file-outcome.model';
import { WorkspacePullState } from '@common/workspace/models/workspace-pull-state.model';
import { AdminWorkspacePullSubscriberService } from '@portal-core/collab/services/admin-workspace-pull-subscriber/admin-workspace-pull-subscriber.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { WorkspacePullProgressService } from '@portal-core/workspaces/services/workspace-pull-progress.service';
import { WorkspaceSessionService } from '@portal-core/workspaces/services/workspace-session.service';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, Observable, of, skip, Subscription, switchMap, take, timer } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

// Idempotent. The shared mcDate pipe also calls this; doing it here decouples our use of
// dayjs.fromNow from that pipe's load order.
dayjs.extend(relativeTime);

/**
 * How often the "Started by … 2 minutes ago" relative timestamp re-evaluates. 10s is a
 * compromise: short enough that the displayed value feels live during a long pull, long
 * enough not to thrash change detection.
 */
const StartedAgoRefreshIntervalMS = 10_000;

export interface CollabPullDialogRow {
  filePath: string;
  /** Per-file terminal outcome once the consumer has reported it. Undefined means the file is
   * either pre-pull (no pull running) or still pending mid-pull. The template uses `isRunning`
   * to disambiguate spinner vs. no icon. */
  outcome: WorkspacePullFileOutcome | undefined;
  /** The file's remote git status (added / modified / deleted), pulled from the workspace files
   * store on row build. Drives the pre-pull status-column badge via `mc-workspace-file-git-status`
   * so the user can see at a glance which incoming change each row represents (a deletion vs a
   * modification looks identical from the file-path column alone). Undefined when the workspace
   * files store doesn't have the file cached yet — the badge simply doesn't render in that case. */
  remoteGitStatus: GitStatus | undefined;
  /** True when this file currently has uncommitted local changes after its pull outcome. */
  hasUncommittedChanges: boolean;
}

export interface CollabPullDialogRowIcon {
  fontIcon: string;
  color?: 'primary' | 'warn' | 'caution' | '';
  cssClass?: string;
  tooltip: string;
}

export interface CollabPullDialogData {
  branchName: string;
  licenseId: number;
  projectId: number;
  userId: string;
  /**
   * When true the dialog runs in observer mode: it reads pull state from
   * {@link AdminWorkspacePullSubscriberService} (which owns its own socket scoped to this
   * workspace) instead of `WorkspacePullProgressService`, hides the Pull action, and never
   * renders the pre-pull file list. Used by the portal-admin pulls grid where no workspace
   * session is bound to the clicked workspace.
   */
  adminView?: boolean;
  /**
   * Optional snapshot of the pull state at the moment the dialog opens. Used in admin view to
   * seed the subscriber so the dialog renders immediately instead of blank-flashing for the
   * ~1s it takes the socket to connect and the bootstrap GET to return. The grid already has
   * this data from its API page so it's free to pass through.
   */
  initialState?: WorkspacePullState;
}

@Component({
  selector: 'mc-collab-pull-dialog',
  templateUrl: './collab-pull-dialog.component.html',
  styleUrls: ['./collab-pull-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollabPullDialogComponent extends DialogBase implements OnInit, OnDestroy {
  static DialogConfig: MatDialogConfig = {
    width: '92rem',
    minHeight: '37rem'
  };

  /**
   * Live file list for the dialog body. Emits the current set of files with remote changes any time
   * the workspace session updates the set — so if a new external commit lands while the dialog is
   * open, the user sees the updated list and pulling reflects the actual current state instead of a
   * stale snapshot.
   */
  filePaths$: Observable<string[]>;

  /**
   * Per-row view model: the union of `filePathsWithRemoteChanges$` (the snapshot of what's pending)
   * and `fileOutcomes$` (the per-file terminal results the consumer has reported so far). Files that
   * complete but weren't in the original snapshot are still surfaced so the dialog never silently
   * drops a row that actually ran. Sorted folders-first for visual stability across emissions.
   */
  rows$: Observable<CollabPullDialogRow[]>;

  pullState$: Observable<WorkspacePullState | null>;

  /**
   * Admin-view only: true when the dialog's workspace has no live pull-state record in Redis
   * (TTL aged out before the user opened the dialog) so the seed from the grid is the most
   * recent data we'll ever have. Drives a dedicated "outcome no longer tracked" view instead
   * of leaving the user staring at an inaccurate state.
   */
  stateExpired$: Observable<boolean>;

  /**
   * True when at least one file in the current state has a non-empty merge error message —
   * gates whether the "Issue" column header label is rendered. Without this, a clean pull (or
   * pre-pull) leaves an unexplained "Issue" header sitting above an empty column.
   */
  hasErrors$: Observable<boolean>;

  /** The license user who initiated the running pull, for showing avatar + name in the overlay. */
  pullingUser$: Observable<LicenseUser | null>;

  /**
   * Human-readable relative time since the pull started (e.g. "a few seconds ago", "2 minutes
   * ago"). Re-evaluates every {@link StartedAgoRefreshIntervalMS} so the displayed value stays
   * accurate during a long-running pull without a per-state-emit refresh. Null when no pull is
   * being observed.
   */
  startedAtRelative$: Observable<string | null>;

  /**
   * Human-readable relative time since the pull terminated (e.g. "a few seconds ago"). Re-evaluates
   * on the same tick as {@link startedAtRelative$} so a left-open dialog ages its "completed X ago"
   * line. Null until the terminal state arrives.
   */
  completedAtRelative$: Observable<string | null>;

  /** True when at least one row should render the In Progress user-status indicator dot. */
  hasUserStatusColumn$: Observable<boolean>;

  /**
   * Captures the most recent terminal pull state (`completed` or `failed`) for as long as the
   * dialog is open, so the dialog can render a completion / failure UI instead of falling back
   * to the original "go pull these files" view. The service-side `pullState$` clears to `null`
   * after a short linger, but the dialog wants to remember the outcome until the user dismisses it.
   * Cleared when the user clicks Done / Close.
   */
  terminalState$: BehaviorSubject<WorkspacePullState | null> = new BehaviorSubject<WorkspacePullState | null>(null);

  /** Subscription that captures terminal states from the service into `terminalState$`. */
  pullStateSubscription: Subscription;

  constructor(
    protected dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: CollabPullDialogData,
    protected dialogRef: MatDialogRef<CollabPullDialogComponent>,
    private adminPullSubscriber: AdminWorkspacePullSubscriberService,
    private licenseUsersService: LicenseUsersService,
    private workspacePullProgressService: WorkspacePullProgressService,
    private workspaceSessionService: WorkspaceSessionService
  ) {
    super(dialog, dialogRef);
    // In admin view the dialog is decoupled from any active WorkspaceSessionService and reads
    // pull state from its own per-dialog subscriber (started in ngOnInit, stopped in ngOnDestroy).
    // Outside admin view, the existing root WorkspacePullProgressService remains the source.
    this.pullState$ = this.data.adminView
      ? this.adminPullSubscriber.pullState$
      : this.workspacePullProgressService.pullState$;

    this.stateExpired$ = this.data.adminView
      ? this.adminPullSubscriber.stateExpired$
      : of(false);

    // Admin view never renders the pre-pull file list — `state.filePaths` from the running /
    // terminal pull is the authoritative list — so collapse the source to an empty stream.
    this.filePaths$ = this.data.adminView
      ? of<string[]>([])
      : this.workspaceSessionService.filePathsWithRemoteChanges$.pipe(
        map(remoteChanges => Array.from(remoteChanges.keys()).sort(CollabPullDialogComponent.comparePathsFoldersFirst))
      );

    // Row list per phase:
    //  - **Pre-pull** (no running and no captured terminal): rows reflect `filePathsWithRemoteChanges$`
    //    live so newly-detected remote changes appear in the table immediately, with each row's
    //    `remoteGitStatus` read straight from the same map the server returned.
    //  - **Running / terminal** (observed): rows are locked to the authoritative `state.filePaths`
    //    snapshot the consumer recorded via `setFilePathsAsync`. New files becoming pullable
    //    mid-pull no longer leak into the in-progress pull's table — they'll surface the next
    //    time the user opens the dialog in pre-pull state. The git-status badge isn't relevant
    //    once a pull is observed (the outcome icon / spinner takes that cell), so we don't
    //    bother resolving `remoteGitStatus` for those rows.
    //
    // `trackBy` on the mat-table reuses the `<tr>` for an unchanged filePath, so the typical
    // emission (same paths, outcomes updating) updates icons/error cells in place without
    // recreating DOM. Rows that genuinely change identity (added/removed when the dialog flips
    // between phases) are the only ones reconciled.
    this.rows$ = combineLatest([
      this.data.adminView ? of(new Map<string, GitStatus>()) : this.workspaceSessionService.filePathsWithRemoteChanges$,
      this.pullState$,
      this.terminalState$
    ]).pipe(
      map(([remoteChanges, live, terminal]) => {
        // Once we've captured a terminal for the pull this dialog was opened against, lock the
        // view to that snapshot. A subsequent pull initiated by another user/tab on the same
        // workspace will re-emit `live` as `running`, but the dialog must keep displaying the
        // outcome the user was looking at until they dismiss it. Pre-pull means both are null.
        const observed: WorkspacePullState | null = terminal ?? live;
        const outcomes: Record<string, WorkspacePullFileState> = observed?.fileOutcomes ?? {};
        const paths = observed ? observed.filePaths ?? [] : Array.from(remoteChanges.keys());
        return paths
          .slice()
          .sort(CollabPullDialogComponent.comparePathsFoldersFirst)
          .map<CollabPullDialogRow>(filePath => ({
            filePath,
            outcome: outcomes[filePath]?.outcome,
            // The status badge only renders for pre-pull rows (the outcome icon / spinner takes
            // the cell once a pull is observed), so we only need to resolve `remoteGitStatus`
            // when `observed` is null. Reading from the same Map that produced the path list
            // means we never have to cross-reference `workspace_files` — which is a dirty-state
            // mirror and doesn't have entries for committed-then-remotely-deleted files.
            remoteGitStatus: observed ? undefined : remoteChanges.get(filePath),
            hasUncommittedChanges: outcomes[filePath]?.hasUncommittedChanges === true
          }));
      })
    );

    this.hasUserStatusColumn$ = this.rows$.pipe(
      map(rows => rows.some(row => this.showsInProgressMarker(row)))
    );

    // True when at least one file has an error message worth showing in the Issue column. The
    // column header label is gated on this so a clean pull (or pre-pull) doesn't carry an empty
    // "Issue" header for users to wonder about.
    this.hasErrors$ = combineLatest([this.pullState$, this.terminalState$]).pipe(
      map(([live, terminal]) => {
        const state = terminal ?? live;
        const hasLiveErrors = Object.values(state?.fileOutcomes ?? {}).some(fileOutcome =>
          fileOutcome.outcome === 'merge-error' || !!fileOutcome.errorMessage
        );
        if (hasLiveErrors) return true;

        const merged = state?.result?.conflictResolutionResult?.merged;
        return merged?.some(m => !m.success && !!m.error?.message) ?? false;
      })
    );

    // Resolves to the initiator's LicenseUser for both running AND terminal states — the post-pull
    // summary keeps the "Started by ..." avatar visible alongside the 100% progress bar so the
    // header looks structurally identical to the during-pull header. `terminalState$` is a class
    // field initialized above this constructor, so it's safe to reference here. The startedByUserId
    // is captured during running/terminal/null transitions; `distinctUntilChanged` keeps us from
    // re-resolving on every progress event.
    this.pullingUser$ = combineLatest([this.pullState$, this.terminalState$]).pipe(
      map(([live, terminal]) => terminal?.startedByUserId ?? live?.startedByUserId ?? null),
      distinctUntilChanged(),
      switchMap(userId => {
        if (!userId) {
          return of<LicenseUser | null>(null);
        }
        return this.licenseUsersService.getLicenseUserByUserId$(userId, this.data.licenseId);
      })
    );

    // Relative "started X ago" string, re-evaluated on a 10-second tick so a long-running pull
    // doesn't display a stale "a few seconds ago" for minutes. Combines the startedAt timestamp
    // (from live or terminal state) with a timer; the async pipe in the template picks up each
    // tick automatically. `timer(0, …)` emits immediately so the value is set on first render.
    this.startedAtRelative$ = combineLatest([
      combineLatest([this.pullState$, this.terminalState$]).pipe(
        map(([live, terminal]) => terminal?.startedAt ?? live?.startedAt ?? null),
        distinctUntilChanged()
      ),
      timer(0, StartedAgoRefreshIntervalMS)
    ]).pipe(
      map(([startedAt]) => startedAt ? dayjs(startedAt).fromNow() : null)
    );

    // Relative "completed X ago" string for the terminal state only. Mirrors `startedAtRelative$`
    // but sourced from `terminalState$` so it doesn't render during a running pull (the live
    // `pullState$` carries no `completedAt`). Shares the same refresh tick.
    this.completedAtRelative$ = combineLatest([
      this.terminalState$.pipe(
        map(terminal => terminal?.completedAt ?? null),
        distinctUntilChanged()
      ),
      timer(0, StartedAgoRefreshIntervalMS)
    ]).pipe(
      map(([completedAt]) => completedAt ? dayjs(completedAt).fromNow() : null)
    );
  }

  ngOnInit() {
    super.ngOnInit();

    // Admin view owns the lifecycle of its own socket subscriber — start it before any
    // `pullState$` subscriptions trigger so the bootstrap GET /pull/state has a chance to seed
    // state before the `skip(1)` below filters out the initial null.
    if (this.data.adminView) {
      this.adminPullSubscriber.start(this.data.projectId, this.data.branchName, this.data.initialState ?? null);
    }

    // If the caller handed us an already-terminal initial state, seed `terminalState$` directly
    // so the dialog renders the post-pull layout (completed-relative caption, error summary,
    // etc.) on first frame instead of waiting for the `pullStateSubscription` below to capture
    // it on a subsequent emission. Two distinct cases this matters for:
    //   - Admin opened the dialog on a row whose pull-state record has since fallen out of
    //     Redis (>120s after completion): bootstrap returns null and no socket events fire, so
    //     the capture below never triggers — without this seed the dialog stays in mid-pull
    //     layout forever.
    //   - The seeded terminal IS still in Redis: avoids a one-tick flash where the header
    //     reflows once the terminal capture pops the `completedAtRelative$` caption in.
    const seed = this.data.initialState;
    if (seed && (seed.status === 'completed' || seed.status === 'failed')) {
      this.terminalState$.next(seed);
    }

    // Capture the first terminal pull state that arrives after the dialog opens, then stop
    // listening. `pullState$` is a `BehaviorSubject` that synchronously replays its current value
    // to new subscribers — `skip(1)` drops that initial value so a stale terminal state from a
    // previous pull (still inside the service's linger window) doesn't make a freshly-opened
    // dialog show the post-pull summary view. `take(1)` after the terminal-status filter freezes
    // the capture to the pull this dialog observed: a later pull initiated by another user on the
    // same workspace can't overwrite the outcome the user is reading.
    this.pullStateSubscription = this.pullState$.pipe(
      skip(1),
      filter(state => state?.status === 'completed' || state?.status === 'failed'),
      take(1)
    ).subscribe(state => this.terminalState$.next(state));
  }

  ngOnDestroy() {
    this.pullStateSubscription?.unsubscribe();
    if (this.data.adminView) {
      this.adminPullSubscriber.stop();
    }
  }

  // Folders-first, case-insensitive alphabetical comparator for slash-delimited paths.
  // Walks segments together so that at any level a subfolder sorts before a file sibling.
  private static comparePathsFoldersFirst(a: string, b: string): number {
    const aSegs = a.split('/');
    const bSegs = b.split('/');
    const min = Math.min(aSegs.length, bSegs.length);
    for (let i = 0; i < min; i++) {
      if (aSegs[i] !== bSegs[i]) {
        const aIsFolderHere = i < aSegs.length - 1;
        const bIsFolderHere = i < bSegs.length - 1;
        if (aIsFolderHere !== bIsFolderHere) {
          return aIsFolderHere ? -1 : 1;
        }
        return aSegs[i].localeCompare(bSegs[i], undefined, { sensitivity: 'base' });
      }
    }
    return aSegs.length - bSegs.length;
  }

  /** True while a running state is in flight. Used to disable the Pull button and show the bar. */
  isRunning(state: WorkspacePullState | null): boolean {
    return state?.status === 'running';
  }

  /** True when no pull is currently active or terminal — the dialog should render the pre-pull
   *  anchor + intro caption rather than the avatar/progress/caption header. */
  isPrePull(state: WorkspacePullState | null, terminal: WorkspacePullState | null): boolean {
    return !terminal && !this.isRunning(state);
  }

  /** "Workspace up to date" terminal state: pull finished with no unresolved AND no resolved
   *  files (e.g. the gate-check short-circuited because nothing actually needed pulling).
   *  Rendered as a centered hero view with no table, since there's no file list to show.
   *  Clean completions WITH resolved files flow through the shared table view so the user
   *  can still see which files were merged. */
  isWorkspaceUpToDateTerminal(terminal: WorkspacePullState | null): boolean {
    return terminal?.status === 'completed'
      && this.unresolvedFilePaths(terminal).length === 0
      && this.resolvedFilePaths(terminal).length === 0;
  }

  /**
   * Reuses the existing workspace-up-to-date hero in one additional case: non-admin pre-pull
   * with zero rows. This prevents the dialog from rendering an empty table if live remote-change
   * updates remove the last row while the dialog is open.
   */
  shouldShowWorkspaceUpToDateView(state: WorkspacePullState | null, terminal: WorkspacePullState | null, rows: CollabPullDialogRow[]): boolean {
    if (this.isWorkspaceUpToDateTerminal(terminal)) {
      return true;
    }
    return !this.data.adminView && this.isPrePull(state, terminal) && rows.length === 0;
  }

  /** Failure terminal state: pull errored out. Separate centered hero view, no table. */
  isFailedTerminal(terminal: WorkspacePullState | null): boolean {
    return terminal?.status === 'failed';
  }

  /**
   * State that drives the active header bindings (avatar / progress bar / caption). Captured
   * terminal takes precedence over live so transitioning into the post-pull view doesn't
   * briefly fall back to live values while terminalState$ catches up. Used by the unified
   * during-pull + partial-complete-terminal header to bind progress bar color/value and
   * caption text without tearing down DOM as the pull transitions.
   */
  activeHeaderState(state: WorkspacePullState | null, terminal: WorkspacePullState | null): WorkspacePullState | null {
    return terminal ?? state;
  }

  /**
   * State to pass into the table's row template for error-message lookup. Terminal wins (post-
   * pull rows show the captured error messages). During a running pull, the live state is fine.
   * Pre-pull (no observed state) returns null so stale `pullState$` data — which the service's
   * bootstrap may carry from Redis's lingering TTL — can't bleed into the Issue column.
   */
  stateForRowErrors(state: WorkspacePullState | null, terminal: WorkspacePullState | null): WorkspacePullState | null {
    if (terminal) return terminal;
    if (this.isRunning(state)) return state;
    return null;
  }

  /** Color binding for the unified header's progress bar:
   *  - accent-vivid during live progress
   *  - primary at clean-completion (positive — everything merged)
   *  - caution at partial-complete-terminal (some files still need attention) */
  headerProgressBarColor(state: WorkspacePullState | null): 'accent-vivid' | 'caution' | 'primary' {
    if (state?.status === 'completed') {
      return this.unresolvedFilePaths(state).length > 0 ? 'caution' : 'primary';
    }
    if (state?.status === 'failed') return 'caution';
    return 'accent-vivid';
  }

  /** Value binding for the unified header's progress bar — live percent during pull, pinned to
   *  100 once terminal so the bar reads as "done." */
  headerProgressBarValue(state: WorkspacePullState | null): number {
    if (state?.status === 'completed' || state?.status === 'failed') return 100;
    return this.progressPercent(state);
  }

  /**
   * Caption text under the unified header's progress bar. Pre-pull state never reaches this
   * (the pre-pull anchor renders instead). For terminal states the caption folds in the workflow
   * "Status changed to In Progress" notice when applicable so the dialog doesn't carry two
   * separate stacked prompts and the table doesn't shift when the notice first becomes
   * applicable mid-pull.
   *
   * Clean-completion with N>0 resolved files: celebratory count ("N files updated", optionally
   * "moved to In Progress"). Partial-complete: actionable guidance about the flagged files.
   * The workspace-up-to-date case (0 unresolved + 0 resolved) is rendered as a centered hero
   * view elsewhere and never reaches here.
   */
  headerCaptionText(state: WorkspacePullState | null): string {
    if (state?.status === 'completed') {
      const unresolved = this.unresolvedFilePaths(state).length;
      const inProgressCount = this.inProgressFilePaths(state).length;
      const deletedCount = this.deletedFilePaths(state).length;
      const noOpCount = this.noOpFilePaths(state).length;
      if (unresolved === 0) {
        // Clean completion with files. Separate the counts because only merges + pending-adds
        // actually land at workspace-file status "In Progress" — deletes remove the
        // workspace_file row entirely so there's nothing to hold an In-Progress state. The trash
        // icons on the rows convey the deletion side; the caption focuses on the count that needs
        // the user's attention (the In-Progress files they may want to review or commit).
        if (inProgressCount > 0) {
          const fileWord = inProgressCount === 1 ? 'file' : 'files';
          return `Pull complete. ${inProgressCount} ${fileWord} moved to In Progress.`;
        }
        // Clean completion where files merged but none ended with local uncommitted state,
        // and there were no deletes or no-ops to summarize.
        if (deletedCount === 0 && noOpCount === 0) {
          return 'Pull complete.';
        }
        // All resolutions were deletes (no merges, no pending-adds). Avoid the "N files updated"
        // phrasing the previous code would fall through to — nothing was updated, files were removed.
        if (deletedCount === 0 && noOpCount > 0) {
          const fileWord = noOpCount === 1 ? 'file' : 'files';
          return `Pull complete. ${noOpCount} ${fileWord} already up to date.`;
        }
        const fileWord = deletedCount === 1 ? 'file' : 'files';
        return `Pull complete. ${deletedCount} ${fileWord} removed.`;
      }
      // Partial completion — some merges failed.
      return inProgressCount > 0
        ? 'Pull complete. Merged files moved to In Progress. Please correct the flagged files in an external commit before pulling again.'
        : 'Pull complete. Please correct the flagged files in an external commit before pulling again.';
    }
    if (state?.status === 'failed') {
      // Defensive — failed terminal renders the centered failure hero, not this caption.
      return 'Pull failed.';
    }
    // Phase-specific caption for a running pull. 'preparing' covers the silent window before
    // the consumer has resolved the file list (acquire lock → fetch auth token → compute
    // remote-change set against fresh workspace state). 'pulling' is the active per-file work.
    if (state?.phase === 'preparing') {
      return 'Computing changes…';
    }
    return 'Pulling changes…';
  }

  /** Conditional CSS classes for the header caption:
   *  - muted during a live pull (status indicator)
   *  - primary at clean-completion (positive feedback)
   *  - bold caution at partial-complete (actionable next-step text) */
  headerCaptionCssClasses(state: WorkspacePullState | null): Record<string, boolean> {
    const isCompleted = state?.status === 'completed';
    const hasUnresolved = isCompleted && this.unresolvedFilePaths(state).length > 0;
    const isClean = isCompleted && !hasUnresolved;
    return {
      'mc-text-muted': !isCompleted,
      'mc-text-primary': isClean,
      'text-heavy-weight': hasUnresolved,
      'mc-text-caution': hasUnresolved
    };
  }

  /**
   * Single progress percentage that advances smoothly across all three pull phases.
   * Delegates to the service so the bar component and the dialog stay in lockstep.
   */
  progressPercent(state: WorkspacePullState | null): number {
    return WorkspacePullProgressService.aggregatePercent(state);
  }

  /** Number of file paths that have reached a terminal per-file outcome. */
  completedFileCount(state: WorkspacePullState | null): number {
    return state?.fileOutcomes ? Object.keys(state.fileOutcomes).length : 0;
  }

  /** Total file count denominator shown in the header progress metric. */
  totalFileCount(state: WorkspacePullState | null): number {
    return state?.totalFiles ?? 0;
  }

  /** Number of successfully merged/resolved files in a completed pull. */
  mergedFileCount(state: WorkspacePullState | null): number {
    const completedCount = this.completedFileCount(state);
    const failedCount = this.failedFileCount(state);
    return Math.max(0, completedCount - failedCount);
  }

  /** Number of files that failed to merge in a completed pull. */
  failedFileCount(state: WorkspacePullState | null): number {
    if (state?.status !== 'completed') return 0;
    return this.unresolvedFilePaths(state).length;
  }

  /**
   * Looks up the per-file merge error message from the terminal result. Returns `null` when no
   * error is recorded for the path — either because the file merged cleanly, the pull hasn't
   * finished yet, or the result payload didn't carry detail for that path.
   */
  errorMessageForPath(filePath: string, state: WorkspacePullState | null): string | null {
    const liveMessage = state?.fileOutcomes?.[filePath]?.errorMessage;
    if (liveMessage) return liveMessage;

    const merged = state?.result?.conflictResolutionResult?.merged;
    if (!merged) return null;
    const found = merged.find(m => m.filePath === filePath);
    return found?.error?.message ?? null;
  }

  /**
   * File paths that finished cleanly in a completed pull (merged successfully + soft-deleted +
   * no-op + pending-add). Used by `isWorkspaceUpToDateTerminal` to detect the
   * "0 unresolved + 0 resolved" empty-pull case. Empty array if the state isn't a successful
   * completion.
   */
  resolvedFilePaths(state: WorkspacePullState | null): string[] {
    const r = state?.result?.conflictResolutionResult;
    if (!r) return [];
    return [
      ...r.merged.filter(m => m.success).map(m => m.filePath),
      ...r.deleted.filter(m => m.success).map(m => m.filePath),
      ...r.noOps.filter(m => m.success).map(m => m.filePath),
      ...r.pendingAdds.filter(m => m.success).map(m => m.filePath)
    ];
  }

  /**
   * File paths that landed in workspace-file status "In Progress".
   *
   * Prefer per-file outcomes that explicitly report post-completion uncommitted state:
   * merged/pending-add with `hasUncommittedChanges === true`.
   */
  inProgressFilePaths(state: WorkspacePullState | null): string[] {
    const outcomes = Object.entries(state?.fileOutcomes ?? {});
    return outcomes
      .filter(([, fileState]) =>
        (fileState.outcome === 'merged' || fileState.outcome === 'pending-add')
        && fileState.hasUncommittedChanges === true
      )
      .map(([filePath]) => filePath);
  }

  /**
   * File paths that were soft-removed by the pull (remote-deleted files with no local edits).
   * Used by the post-pull caption when the pull resolved only deletes — "N files removed" is
   * accurate where "N files updated" / "moved to In Progress" would not be.
   */
  deletedFilePaths(state: WorkspacePullState | null): string[] {
    return state?.result?.conflictResolutionResult?.deleted
      ?.filter(m => m.success)
      .map(m => m.filePath) ?? [];
  }

  /**
   * File paths that required no local merge action because local and remote already matched.
   * Used by the completed-pull caption to avoid misleading "0 files removed" text when a
   * completion consists only of no-ops.
   */
  noOpFilePaths(state: WorkspacePullState | null): string[] {
    return state?.result?.conflictResolutionResult?.noOps
      ?.filter(m => m.success)
      .map(m => m.filePath) ?? [];
  }

  /**
   * File paths whose merge failed in a completed pull. Surfaced in the post-pull summary so
   * observers see why the bar still has files in it.
   */
  unresolvedFilePaths(state: WorkspacePullState | null): string[] {
    return state?.result?.conflictResolutionResult?.merged
      ?.filter(m => !m.success)
      .map(m => m.filePath) ?? [];
  }

  /** Closes the dialog after the user acknowledges the post-pull summary. */
  onDoneClicked() {
    this.terminalState$.next(null);
    this.closeDialog(true, true);
  }

  /**
   * Maps a row's outcome to the icon descriptor rendered in the `mcListItemActions` slot, or
   * returns `null` for "render no icon" (no-op outcomes and pre-pull rows). Pending rows during
   * a running pull return `null` here too — the template branches separately to render a spinner
   * so callers don't have to thread a sentinel value through the icon descriptor type.
   */
  getOutcomeIcon(outcome: WorkspacePullFileOutcome | undefined): CollabPullDialogRowIcon | null {
    switch (outcome) {
      case 'merged':
        return { fontIcon: 'icon-checkmark', color: 'primary', tooltip: 'File updated' };
      case 'pending-add':
        return { fontIcon: 'icon-add', color: 'primary', tooltip: 'New workspace file kept' };
      case 'deleted':
        return { fontIcon: 'icon-trash', color: 'primary', tooltip: 'Removed' };
      case 'merge-error':
        return { fontIcon: 'icon-error', color: 'warn', tooltip: 'Merge failed' };
      case 'concurrent-edit':
        return { fontIcon: 'icon-edit', color: 'caution', tooltip: 'Currently being edited' };
      case 'no-op':
        return { fontIcon: 'icon-checkmark', color: '', tooltip: 'Already up to date' };
      case undefined:
      default:
        return null;
    }
  }

  /** True when the row's pull outcome moved the file into In Progress. */
  showsInProgressMarker(row: CollabPullDialogRow): boolean {
    if (!row.hasUncommittedChanges) return false;
    return row.outcome === 'merged' || row.outcome === 'pending-add';
  }

  /** Column order for the file-status `mat-table`. The user-status column is conditionally shown. */
  private readonly baseDisplayedColumns: readonly string[] = ['status', 'path', 'error'];
  private readonly displayedColumnsWithUserStatus: readonly string[] = ['status', 'path', 'error', 'userStatus'];

  displayedColumns(showUserStatusColumn: boolean): readonly string[] {
    return showUserStatusColumn ? this.displayedColumnsWithUserStatus : this.baseDisplayedColumns;
  }

  trackByPath(_index: number, row: CollabPullDialogRow): string {
    return row.filePath;
  }

  onPullClicked(filePaths: string[]) {
    this.workspacePullProgressService.startPull({
      branchName: this.data.branchName,
      clientId: uuidv4(),
      fileType: CollabFileType.Workspace,
      licenseId: this.data.licenseId,
      projectId: this.data.projectId,
      userId: this.data.userId,
    }, filePaths);
  }
}
